import { NavContext } from '@ionic/react';
import { Button } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GoogleButton } from '../../components/auth/GoogleButton/GoogleButton';
import { signInWithGoogle } from '../../configs/firebase/firebase-config';
import { ContextApp } from '../../contexts/ContextApp';
import GraphqlService from '../../services/graphql/GraphqlService';
import './invitation.less';
import { useCustomMessage } from '../../hooks/useCustomMessage';
import { IInvitation } from '../../interfaces/Invitation';
import { IError } from '../../interfaces/environment';
import { IUserFirebase } from '../../interfaces/user';
import {
  useCustomQuery,
  useCustomMutation,
} from '../../hooks/apollo/ApolloCustomHooks';
import { useScopedMutation } from '../../hooks/useScopedMutation';

const InvitationPage = () => {
  const { user, t } = useContext(ContextApp);
  const { navigate } = useContext(NavContext);
  const location = useLocation();
  const replaceLocation = useNavigate();
  const [acceptedInvitation, setAcceptedInvitation] = useState<
    boolean | undefined
  >(undefined);
  const { Query, Mutation } = GraphqlService();
  const invitationUID = useMemo(
    () => new URLSearchParams(location.search).get('invitation_id'),
    [location.search],
  );
  const formSession = useMemo(() => ({}), [invitationUID]);
  const { runMutation } = useScopedMutation(
    invitationUID || undefined,
    'invitation',
    formSession,
  );
  const { messageModalError, getErrorMessage, messageUpdateSuccess } =
    useCustomMessage();
  const onClickLogin = async () => {
    const firebaseUser = await signInWithGoogle();
    if (!firebaseUser || acceptedInvitation === undefined) return;
    await handleResponseInvitation(acceptedInvitation, {
      input: {
        uid: firebaseUser.uid,
        token: await firebaseUser.getIdToken(),
        displayName:
          firebaseUser.displayName ||
          firebaseUser.providerData[0]?.displayName ||
          undefined,
        email:
          firebaseUser.email ||
          firebaseUser.providerData[0]?.email ||
          undefined,
        photoURL:
          firebaseUser.photoURL ||
          firebaseUser.providerData[0]?.photoURL ||
          undefined,
        phoneNumber:
          firebaseUser.phoneNumber ||
          firebaseUser.providerData[0]?.phoneNumber ||
          undefined,
        providerId: firebaseUser.providerData[0]?.providerId,
      },
    });
  };

  const invitationQuery = useCustomQuery<{
    checkInvitation: IInvitation;
  }>(Query.checkInvitation, {
    variables: { invitation_uid: invitationUID },
    skip: !invitationUID,
    context: { queryDeduplication: false },
  });
  const invitationData = invitationQuery.data?.checkInvitation;

  const [invitationResponseMutation] = useCustomMutation<{
    invitationResponse: IInvitation | (IError & { __typename: 'ResultError' });
  }>(Mutation.invitationResponse);

  const handleResponseInvitation = useCallback(
    async (
      accept: boolean,
      userInput?: { uid?: string; input?: IUserFirebase },
    ) => {
      if (!invitationData) return;
      await runMutation({
        channel: 'form',
        send: () =>
          invitationResponseMutation({
            variables: {
              invitation_uid: invitationData.uid,
              accept,
              ...userInput,
            },
          }),
        onSuccess: () => {
          messageUpdateSuccess({
            context: 'Invitation.handleResponseInvitation.1',
          });
          replaceLocation(
            { pathname: location.pathname, search: '', hash: location.hash },
            { replace: true },
          );
          navigate('/');
        },
        onError: (error) =>
          messageModalError({
            error,
            context: 'Invitation.handleResponseInvitation.2',
            message: getErrorMessage(error),
          }),
      });
    },
    [
      invitationData,
      location.hash,
      location.pathname,
      navigate,
      replaceLocation,
      runMutation,
    ],
  );

  useEffect(() => {
    setAcceptedInvitation(undefined);
  }, [invitationUID]);

  useEffect(() => {
    if (acceptedInvitation !== undefined && user) {
      const userInput = { uid: user.uid };
      void handleResponseInvitation(acceptedInvitation, userInput);
    } else if (acceptedInvitation === false) {
      void handleResponseInvitation(acceptedInvitation);
    }
  }, [acceptedInvitation, user, handleResponseInvitation]);

  return (
    <div className="invitation-page">
      {invitationData ? (
        <div className="invitation-page__container">
          {acceptedInvitation === undefined && (
            <div className="accept-invitation">
              <h3>
                {t('abm.acceptInvitationMessage', {
                  replace: {
                    role: invitationData.role?.name,
                    tenant: invitationData.tenant?.name,
                  },
                })}
              </h3>
              <div className="accept-invitation__buttons">
                <Button
                  onClick={() => setAcceptedInvitation(false)}
                  type="default"
                >
                  {t('abm.reject')}
                </Button>
                <Button
                  onClick={() => setAcceptedInvitation(true)}
                  type="primary"
                >
                  {t('abm.accept')}
                </Button>
              </div>
            </div>
          )}
          {acceptedInvitation !== undefined && !user && (
            <GoogleButton onClick={onClickLogin} title={t('abm.loginGoogle')} />
          )}
        </div>
      ) : (
        invitationQuery.error && (
          <div id="invitation-page-response-message">
            <p>{invitationQuery.error.message}</p>
          </div>
        )
      )}
    </div>
  );
};

export default InvitationPage;
